import React, { useState, useMemo, useCallback, useRef } from 'react';
import { View, TouchableOpacity, Pressable, ScrollView } from 'react-native';
import { useNavigation, NavigationProp, ParamListBase } from '@react-navigation/native';
import Text from '@components/CustomText';
import Svg, { Path, Circle as SvgCircle } from 'react-native-svg';
import LinearGradient from 'react-native-linear-gradient';
import { Dimensions } from "react-native";
import { scale, ScaledSheet } from "@utils/scale";
import LoadingComponent from "./LoadingComponent";

const { width: SCREEN_WIDTH } = Dimensions.get('screen');
const CARD_PADDING = scale(12);
const STATIC_LADDER_LENGTH = 5;
const AVAILABLE_WIDTH = Math.max(0, SCREEN_WIDTH - scale(32) - CARD_PADDING * 2);

type LadderIcon = 'CIRCLE' | 'TICK' | 'LOCK' | 'LINE' | string;

type LadderItem = {
    header: number | string;
    header_tooltip?: string;
    icon?: LadderIcon;
    footer: number | string | null;
};

type OfferWidgetData = {
    enabled?: boolean;
    widget_type: string;
    title?: string;
    caption_text?: string;
    caption_2_text?: string;
    caption_2_value?: number | string;
    caption_3_text?: string;
    caption_3_value?: number | string;
    show_caption_3_text?: boolean;
    ladder?: LadderItem[];
    progress?: number;
    total_progress?: number;
    nav_to?: string;
    nav_value?: unknown;
    bottom_text?: string;
    bottom_strip_text?: string;
    footer_banner_text?: string;
    bottom_banner_text?: string;
    loading?: string[];
};

type OfferWidgetProps = {
    data?: OfferWidgetData | null;
};

const OfferWidgetWidget: React.FC<OfferWidgetProps> = ({ data }) => {
    const navigation = useNavigation<NavigationProp<ParamListBase>>();
    if (!data || !data.enabled) return null;

    const widgetType = data?.widget_type;

    const cardColor = widgetType === 'SPECIAL_OFFER' ? '#FF2E00' : '#4CAF50';
    const titleColor = widgetType === 'SPECIAL_OFFER' ? '#FF2E00' : '#000';
    const linearGradientColors = widgetType === 'SPECIAL_OFFER' ? ['#FFFFFF', '#FFFFFF', '#FFE2E2'] : ['#FFFFFF', '#FFFFFF', '#C8E6C9'];
     
    const {
        title,
        caption_text,
        caption_2_text,
        caption_2_value,
        caption_3_text,
        caption_3_value,
        show_caption_3_text,
        ladder = [],
        progress,
        total_progress,
        nav_to,
        nav_value
    } = data;
    const [showTooltip, setShowTooltip] = useState(false);
    const milestoneWidth = scale(30);
    const defaultConnectorWidth = scale(30);

    const bottomStripText = data?.bottom_text || data?.bottom_strip_text || data?.footer_banner_text || data?.bottom_banner_text;

    // Numeric ladder footers (used for spacing and progress mapping)
    const { numericFooters, hasValidFooters } = useMemo(() => {
        const values = (ladder || []).map((item: LadderItem) => {
            const num = Number(item.footer);
            return Number.isFinite(num) ? num : null;
        });
        const valid = values.length === (ladder?.length || 0) && values.every((v) => v !== null);
        return { numericFooters: values, hasValidFooters: valid };
    }, [ladder]);

    // Rebuilt layout logic:
    // - If milestones <= 5: keep everything visible and distribute gaps based on footer deltas.
    // - If milestones > 5: allow scrolling, still use proportional gaps for spacing.
    const {
        ladderLength,
        connectorWidths,
        ladderContentWidth,
        ladderContainerWidth,
        progressTrackWidth,
        centerPositions,
        isSingleMilestone,
        isCompactLadder,
        shouldScroll,
        hasNumericProgress,
    } = useMemo(() => {
        const length = ladder?.length || 0;
        const availableWidth = Math.max(0, SCREEN_WIDTH - scale(32) - CARD_PADDING * 2);
        const isCompact = length > 0 && length <= STATIC_LADDER_LENGTH;

        if (length === 0) {
            return {
                ladderLength: 0,
                connectorWidths: [],
                ladderContentWidth: 0,
                ladderContainerWidth: availableWidth,
                progressTrackWidth: 0,
                centerPositions: [],
                isSingleMilestone: false,
                isCompactLadder: isCompact,
                shouldScroll: false,
                hasNumericProgress: false,
            };
        }

        const minConnectorWidth = scale(36);
        const maxConnectorWidth = scale(availableWidth);
        const useStaticFullWidth = length > 0 && length < STATIC_LADDER_LENGTH;

        // When milestones are fewer than the static ladder length, stretch the layout to full width.
        // Connector length is derived from the full width so there is no unused space.
        if (useStaticFullWidth) {
            const gapCount = Math.max(length - 1, 1);
            const rawConnector = (availableWidth - milestoneWidth * length) / gapCount;
            const connectorWidth = Math.max(minConnectorWidth, Math.min(maxConnectorWidth, rawConnector));
            const staticConnectors = Array.from({ length: Math.max(length - 1, 0) }, () => connectorWidth);
            const connectorTotal = staticConnectors.reduce((sum, w) => sum + w, 0);
            const contentWidth = Math.max(availableWidth, milestoneWidth * length + connectorTotal);

            const centers: number[] = [];
            let cursor = milestoneWidth / 2;
            for (let i = 0; i < length; i++) {
                centers.push(cursor);
                if (i < length - 1) {
                    cursor += milestoneWidth + connectorWidth;
                }
            }

            // Track spans from first center to last center; offset handled via margin in render.
            const trackWidth = Math.max(milestoneWidth, contentWidth - milestoneWidth);

            return {
                ladderLength: length,
                connectorWidths: staticConnectors,
                ladderContentWidth: contentWidth,
                ladderContainerWidth: contentWidth,
                progressTrackWidth: trackWidth,
                centerPositions: centers,
                isSingleMilestone: length === 1,
                isCompactLadder: isCompact,
                shouldScroll: false,
                hasNumericProgress:
                    hasValidFooters &&
                    numericFooters.length === length &&
                    (numericFooters[length - 1]! - numericFooters[0]!) !== 0,
            };
        }

        const milestoneGapMin = minConnectorWidth * Math.max(length - 1, 0);
        const availableGapSpace = Math.max(availableWidth - milestoneWidth * length, milestoneGapMin);

        const hasRange = hasValidFooters && numericFooters.length === length && (numericFooters[length - 1]! - numericFooters[0]!) !== 0;
        const deltas: number[] = [];
        if (length > 1) {
            for (let i = 1; i < length; i++) {
                const diff = hasRange ? Math.max(0, (numericFooters[i] as number) - (numericFooters[i - 1] as number)) : 1;
                deltas.push(diff);
            }
        }

        const deltaSum = deltas.reduce((sum, d) => sum + d, 0) || 1;
        let proportionalConnectors = deltas.map((delta) => (delta / deltaSum) * availableGapSpace);
        proportionalConnectors = proportionalConnectors.map((w) => Math.max(minConnectorWidth, Math.min(maxConnectorWidth, w)));

        // Compact (<=5) proportional layout: map numeric footers to positions to ensure gaps respond to value changes.
        if (isCompact && hasRange) {
            const minVal = numericFooters[0] as number;
            const maxVal = numericFooters[numericFooters.length - 1] as number;
            const compactTrackWidth = Math.max(
                availableWidth,
                milestoneWidth * length + minConnectorWidth * Math.max(length - 1, 0)
            );
            const usableTrack = Math.max(milestoneWidth, compactTrackWidth - milestoneWidth);
            const positions = numericFooters.map((v) => ((v as number) - minVal) / (maxVal - minVal) * usableTrack);

            const compactConnectors: number[] = [];
            for (let i = 0; i < positions.length - 1; i++) {
                const gap = Math.max(minConnectorWidth, Math.min(maxConnectorWidth, positions[i + 1] - positions[i] - milestoneWidth));
                compactConnectors.push(gap);
            }
            proportionalConnectors = compactConnectors;
        } else if (isCompact) {
            // For compact ladders without numeric range, ensure everything fits on screen by shrinking proportionally if needed.
            const initialConnectorTotal = proportionalConnectors.reduce((sum, w) => sum + w, 0);
            const initialContentWidth = milestoneWidth * length + initialConnectorTotal;
            if (initialContentWidth > availableWidth) {
                const allowedGap = Math.max(availableWidth - milestoneWidth * length, minConnectorWidth * Math.max(length - 1, 0));
                const shrinkFactor = allowedGap / Math.max(initialConnectorTotal, 1);
                proportionalConnectors = proportionalConnectors.map((w) => Math.max(minConnectorWidth, Math.min(maxConnectorWidth, w * shrinkFactor)));
            }
        }

        const connectorTotal = proportionalConnectors.reduce((sum, w) => sum + w, 0);
        const contentWidth = milestoneWidth * length + connectorTotal;

        // Build center positions for accurate progress mapping.
        const centers: number[] = [];
        let cursor = milestoneWidth / 2;
        for (let i = 0; i < length; i++) {
            centers.push(cursor);
            if (i < length - 1) {
                cursor += milestoneWidth + proportionalConnectors[i];
            }
        }

        const trackWidth = Math.max(milestoneWidth, contentWidth - milestoneWidth);
        const containerWidth = isCompact ? Math.max(contentWidth, availableWidth) : contentWidth;
        const enableScroll = !isCompact && (length > STATIC_LADDER_LENGTH || contentWidth > availableWidth);

        return {
            ladderLength: length,
            connectorWidths: proportionalConnectors,
            ladderContentWidth: contentWidth,
            ladderContainerWidth: containerWidth,
            progressTrackWidth: trackWidth,
            centerPositions: centers,
            isSingleMilestone: length === 1,
            isCompactLadder: isCompact,
            shouldScroll: enableScroll,
            hasNumericProgress: hasRange,
        };
    }, [CARD_PADDING, milestoneWidth, numericFooters, hasValidFooters, ladder]);

    // Center compact ladders when they fit on screen
    const contentOffset = useMemo(() => {
        if (!isCompactLadder || shouldScroll) return 0;
        return Math.max(0, (ladderContainerWidth - ladderContentWidth) / 2);
    }, [isCompactLadder, shouldScroll, ladderContainerWidth, ladderContentWidth]);

    const isloading = (loadKey: string) => {
        return data?.loading?.includes(loadKey);
    };

    const getProgressPercentage = useCallback(() => {
        if (!progress || !total_progress || total_progress === 0) return 0;
        return (progress / total_progress) * 100;
    }, [progress, total_progress]);

    // Map progress to ladder positions using milestone footers and connector widths for accurate placement
    const calculateProgressPositionPercent = useCallback(() => {
        if (!ladder || ladder.length === 0) return 0;
        if (!hasNumericProgress || centerPositions.length !== ladder.length) {
            return Math.min(100, Math.max(0, getProgressPercentage()));
        }

        const current = Math.max(0, progress || 0);
        const firstVal = numericFooters[0] as number;
        const lastVal = numericFooters[numericFooters.length - 1] as number;

        if (current <= firstVal) return 0;
        if (current >= lastVal) return 100;

        let positionPx = 0;
        for (let i = 1; i < ladder.length; i++) {
            const prevVal = numericFooters[i - 1] as number;
            const nextVal = numericFooters[i] as number;
            const prevCenter = centerPositions[i - 1];
            const nextCenter = centerPositions[i];
            const segmentWidthPx = nextCenter - prevCenter;
            const range = nextVal - prevVal;

            if (current >= nextVal) {
                positionPx += segmentWidthPx;
                continue;
            }

            const ratio = range > 0 ? (current - prevVal) / range : 0;
            positionPx += Math.max(0, Math.min(1, ratio)) * segmentWidthPx;
            break;
        }

        return Math.min(100, Math.max(0, (positionPx / progressTrackWidth) * 100));
    }, [centerPositions, getProgressPercentage, hasNumericProgress, ladder, numericFooters, progress, progressTrackWidth]);

    const renderIcon = useCallback((iconType?: LadderIcon) => {
        switch (iconType) {
            case 'CIRCLE':
                return (
                    <View style={[styles.circleIcon, { backgroundColor: cardColor }]} />
                );
            case 'TICK':
                return (
                    <View style={[styles.tickIcon, { backgroundColor: cardColor }]}>
                        <Svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <Path
                                d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                                fill="#FFFFFF"
                            />
                        </Svg>
                    </View>
                );
            case 'LOCK':
                return (
                    <View style={styles.lockIcon}>
                        <Svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <Path
                                d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"
                                fill="#666666"
                            />
                        </Svg>
                    </View>
                );
            case 'LINE':
                return <View style={styles.lineIcon} />;
            default:
                return null;
        }
    }, []);

    const renderMilestone = useCallback((item: LadderItem, index: number) => {
        const isFirstItem = index === 0;
        const isLastItem = index === ladderLength - 1;

        return (
            <View key={index} style={[
                styles.milestoneContainer,
                isFirstItem && styles.firstMilestone,
                isLastItem && styles.lastMilestone
            ]}>
                {/* Header (reward amount or MG) */}
                <View style={styles.headerContainer}>
                    <LoadingComponent
                        visible={!isloading(`ladder`)}
                        style={{
                            height: scale(18),
                            width: scale(50),
                        }}
                    >
                        {item.header !== 0 ? (
                            item.header === 'MG' ? (
                                <View style={styles.mgContainer}>
                                    <Text style={styles.mgText}>MG</Text>
                                    <View style={styles.tooltipWrapper}>
                                        <TouchableOpacity
                                            style={styles.infoIcon}
                                            onPress={() => setShowTooltip(!showTooltip)}
                                        >
                                            <Svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                                <SvgCircle cx="12" cy="12" r="10" fill="#666666" />
                                                <Path
                                                    d="M11 7h2v2h-2V7zm0 4h2v6h-2v-6z"
                                                    fill="#FFFFFF"
                                                />
                                            </Svg>
                                        </TouchableOpacity>
                                        {showTooltip && item.header_tooltip && (
                                            <View style={styles.tooltip}>
                                                <Text style={styles.tooltipText}>{item.header_tooltip}</Text>
                                                <View style={styles.tooltipArrow} />
                                            </View>
                                        )}
                                    </View>
                                </View>
                            ) : (
                                <Text style={[styles.rewardText]}>
                                    {item.header}
                                </Text>
                            )
                        ) : null}
                    </LoadingComponent>
                </View>

                {/* Icon */}
                <View style={styles.iconContainer}>
                    {renderIcon(item.icon)}
                </View>

                {/* Footer (trip count) */}
                <View style={styles.footerContainer}>
                    <LoadingComponent
                        visible={!isloading(`ladder`)}
                        style={{
                            height: 16,
                            width: 30,
                        }}
                    >
                        {item.footer !== null && (
                            <Text style={styles.footerText} numberOfLines={1}>{item.footer}</Text>
                        )}
                    </LoadingComponent>
                </View>
            </View>
        );
    }, [isloading, ladderLength, renderIcon, showTooltip]);

    const progressPercentage = getProgressPercentage();
    const trackProgressPercent = Math.min(100, Math.max(0, calculateProgressPositionPercent()));
    const clampedProgress = Math.min(100, Math.max(0, progressPercentage));
    const progressLabelWidth = scale(35);
    const progressBaseLeft = milestoneWidth / 2 + contentOffset;
    const progressLabelLeft = useMemo(() => {
        const px = (progressTrackWidth * trackProgressPercent) / 100;
        return Math.max(
            0,
            Math.min(progressTrackWidth - progressLabelWidth, px - progressLabelWidth / 2)
        );
    }, [progressLabelWidth, progressTrackWidth, trackProgressPercent]);

    const scrollRef = useRef<ScrollView | null>(null);
    const scrollToLeft = useCallback(() => {
        scrollRef.current?.scrollTo({ x: 0, y: 0, animated: true });
    }, []);
    const scrollToRight = useCallback(() => {
        scrollRef.current?.scrollToEnd({ animated: true });
    }, []);

    const showScrollHint = (!isCompactLadder && ladderContentWidth > AVAILABLE_WIDTH) || (isCompactLadder && shouldScroll);

    return (
        <View style={[styles.container, { backgroundColor: cardColor }]}>
            <LinearGradient
                colors={linearGradientColors}
                locations={[0, 0.6, 1]}
                style={[styles.card]}
                start={{ x: 0, y: 0 }}
                end={{ x: 0, y: 1 }}
            >
                <Pressable onPress={() => nav_to && navigation.navigate(nav_to, { nav_value: nav_value })}>
                    {/* Title */}
                    <Text variant="title_20" style={[styles.title, { color: titleColor }]}>{title}</Text>
                    {/* Divider Line */}
                    <View style={styles.divider} />
                </Pressable>

                {/* Caption */}
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Text numberOfLines={2} style={styles.caption}>{caption_text}</Text>
                    <View style={styles.scrollWrapper}>
                        {showScrollHint ? (
                            <View  style={{ flexDirection: 'row' }}>
                                
                                <Pressable onPress={() => {
                                    scrollToLeft();
                                }}>
                                    <Text style={styles.scrollHintText}>←</Text>
                                </Pressable>
                                <Pressable onPress={() => {
                                    scrollToRight();
                                }} style={{ marginLeft: 10 }}>
                                    <View pointerEvents="none" style={styles.scrollHint}>
                                        <Text style={styles.scrollHintText}>→</Text>
                                    </View>
                                </Pressable>
                            </View>
                        ) : null}


                    </View>
                </View>

                {/* Ladder Container */}
                <View style={styles.ladderWrapper}>
                    {isCompactLadder ? (
                        shouldScroll ? (

                            <ScrollView
                            ref={scrollRef}
                                horizontal
                                showsHorizontalScrollIndicator={false}
                                contentContainerStyle={[styles.ladderScrollContent, { paddingRight: milestoneWidth }]}
                            >
                                <View style={[styles.ladderContent, { width: ladderContentWidth }]}>
                                    {/* Progress Line Background */}
                                    <View style={[
                                        styles.progressLineContainer,
                                        {
                                            width: progressTrackWidth,
                                            //   marginLeft: progressBaseLeft,
                                        }
                                    ]}>
                                        <View style={styles.progressLineBackground} />
                                        <LoadingComponent
                                            visible={!isloading("progress")}
                                            style={{
                                                position: 'absolute',
                                                height: scale(4),
                                                backgroundColor: cardColor,
                                                borderRadius: 2,
                                                width: '100%'
                                            }}
                                        >
                                            <View
                                                style={[
                                                    styles.progressLineFilled,
                                                    { width: `${trackProgressPercent}%`, backgroundColor: cardColor }
                                                ]}
                                            />
                                        </LoadingComponent>
                                    </View>
                                    {/* Milestones */}
                                    <LoadingComponent
                                        visible={!isloading("ladder")}
                                        style={{
                                            height: 70,
                                            width: '100%',
                                        }}
                                    >
                                        <View style={[styles.milestonesRow, { width: ladderContentWidth }]}>
                                            {ladder.map((item: LadderItem, index: number) => (
                                                <React.Fragment key={index}>
                                                    <View style={isSingleMilestone ? styles.singleMilestone : null}>
                                                        {renderMilestone(item, index)}
                                                    </View>
                                                    {index < ladder.length - 1 && item.icon !== 'LINE' && (
                                                        <View style={[styles.connector, { width: connectorWidths[index] || defaultConnectorWidth }]} />
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </View>
                                        {(3 < trackProgressPercent && trackProgressPercent < 97) ? (
                                            <View
                                                style={[
                                                    styles.progressLabelContainer,
                                                    {
                                                        left: progressBaseLeft + progressLabelLeft,
                                                        width: progressLabelWidth,
                                                    }
                                                ]}
                                            >
                                                <Text style={[styles.footerProgress]} numberOfLines={1}>{progress}</Text>
                                            </View>
                                        ) : null}
                                    </LoadingComponent>
                                </View>
                            </ScrollView>


                        ) : (
                            <View style={[styles.ladderContent, { width: ladderContainerWidth }]}>
                                {/* Progress Line Background */}
                                <View style={[
                                    styles.progressLineContainer,
                                    {
                                        width: progressTrackWidth,
                                        marginLeft: progressBaseLeft,
                                    }
                                ]}>
                                    <View style={styles.progressLineBackground} />
                                    <LoadingComponent
                                        visible={!isloading("progress")}
                                        style={{
                                            position: 'absolute',
                                            height: scale(4),
                                            backgroundColor: cardColor,
                                            borderRadius: 2,
                                            width: '100%'
                                        }}
                                    >
                                        <View
                                            style={[
                                                styles.progressLineFilled,
                                                { width: `${trackProgressPercent}%`, backgroundColor: cardColor }
                                            ]}
                                        />
                                    </LoadingComponent>
                                </View>
                                {/* Milestones */}
                                <LoadingComponent
                                    visible={!isloading("ladder")}
                                    style={{
                                        height: 70,
                                        width: '100%',
                                    }}
                                >
                                    <View style={[styles.milestonesRow, { width: ladderContentWidth, marginLeft: contentOffset }]}>
                                        {ladder.map((item: LadderItem, index: number) => {
                                            return (<React.Fragment key={index}>
                                                <View style={isSingleMilestone ? styles.singleMilestone : null}>
                                                    {renderMilestone(item, index)}
                                                </View>
                                                {index < ladder.length - 1 && (
                                                    <View style={[styles.connector, { width: connectorWidths[index] || defaultConnectorWidth }]} />
                                                )}
                                            </React.Fragment>)
                                        })}
                                    </View>
                                    {(3 < trackProgressPercent && trackProgressPercent < 97) ? (
                                        <View
                                            style={[
                                                styles.progressLabelContainer,
                                                {
                                                    left: progressBaseLeft + progressLabelLeft,
                                                    width: progressLabelWidth,
                                                }
                                            ]}
                                        >
                                            <Text style={[styles.footerProgress]} numberOfLines={1}>{progress}</Text>
                                        </View>
                                    ) : null}
                                </LoadingComponent>
                            </View>
                        )
                    ) : (
                        <ScrollView
                            horizontal
                            ref={scrollRef}
                            showsHorizontalScrollIndicator={false}
                            contentContainerStyle={[styles.ladderScrollContent, { paddingRight: milestoneWidth }]}
                        >
                            <View style={[styles.ladderContent, { width: ladderContentWidth }]}>
                                {/* Progress Line Background */}
                                <View style={[
                                    styles.progressLineContainer,
                                    {
                                        width: progressTrackWidth,
                                        marginLeft: progressBaseLeft,
                                    }
                                ]}>
                                    <View style={styles.progressLineBackground} />
                                    <LoadingComponent
                                        visible={!isloading("progress")}
                                        style={{
                                            position: 'absolute',
                                            height: scale(4),
                                            backgroundColor: cardColor,
                                            borderRadius: 2,
                                            width: '100%'
                                        }}
                                    >
                                        <View
                                            style={[
                                                styles.progressLineFilled,
                                                { width: `${trackProgressPercent}%`, backgroundColor: cardColor }
                                            ]}
                                        />
                                    </LoadingComponent>
                                </View>
                                {/* Milestones */}
                                <LoadingComponent
                                    visible={!isloading("ladder")}
                                    style={{
                                        height: 70,
                                        width: '100%',
                                    }}
                                >
                                    <View style={[styles.milestonesRow, { width: ladderContentWidth, marginLeft: contentOffset }]}>
                                        {ladder.map((item, index) => {
                                            return (<React.Fragment key={index}>
                                                <View style={isSingleMilestone ? styles.singleMilestone : null}>
                                                    {renderMilestone(item, index)}
                                                </View>
                                                {index < ladder.length - 1 && (
                                                    <View style={[styles.connector, { width: connectorWidths[index] || defaultConnectorWidth }]} />
                                                )}
                                            </React.Fragment>)
}                                       )}
                                    </View>
                                    {(3 < trackProgressPercent && trackProgressPercent < 97) ? (
                                        <View
                                            style={[
                                                styles.progressLabelContainer,
                                                {
                                                    left: progressBaseLeft + progressLabelLeft,
                                                    width: progressLabelWidth,
                                                }
                                            ]}
                                        >
                                            <Text style={[styles.footerProgress]} numberOfLines={1}>{progress}</Text>
                                        </View>
                                    ) : null}
                                </LoadingComponent>
                            </View>
                        </ScrollView>
                    )}
                </View>

                {/* Delivery Percentage and optional caption 3 */}
                <View style={{ flexDirection: "row", justifyContent: 'space-between' }}>
                    <View style={{ flexDirection: "row" }}>
                        <Text style={{ fontSize: 12 }}>{caption_2_text}</Text>
                        <LoadingComponent
                            visible={!isloading("caption_2_value")}
                            style={{
                                height: 14,
                                width: 40,
                                marginLeft: 4,
                            }}
                        >
                            <Text style={{ fontSize: 12 }}>{` ${caption_2_value}`}</Text>
                        </LoadingComponent>
                    </View>
                    {(show_caption_3_text || caption_3_text || caption_3_value) ? (
                        <View style={styles.captionRow}>
                            {caption_3_text ? (
                                <Text style={styles.captionLabel}>{caption_3_text}</Text>
                            ) : null}
                            {caption_3_value ? (
                                <LoadingComponent
                                    visible={!isloading("caption_3_value")}
                                    style={{
                                        height: 14,
                                        width: 60,
                                        marginLeft: caption_3_text ? 4 : 0,
                                    }}
                                >
                                    <Text style={styles.captionValue}>{` ${caption_3_value}`}</Text>
                                </LoadingComponent>
                            ) : null}
                        </View>
                    ) : null}
                </View>
            </LinearGradient>
            {bottomStripText ? (
                <View style={styles.bottomStrip}>
                    <Text style={styles.bottomStripText} numberOfLines={1}>
                        {bottomStripText}
                    </Text>
                </View>
            ) : null}
        </View>
    );
};

const styles = ScaledSheet.create({
    container: {
        width: SCREEN_WIDTH - scale(32),
        borderWidth: 1,
        borderColor: "rgba(0, 0, 0, 0.2)",
        borderRadius: 24,
        marginHorizontal: 6,
        marginVertical: 8,
    },
    card: {
        borderRadius: 24,
        padding: 12,
        backgroundColor: '#FFF',
        overflow: 'hidden',
        elevation: 1
    },
    title: {
        marginLeft: 4,
        marginBottom: 8,
    },
    divider: {
        height: 1,
        backgroundColor: '#E0E0E0',
        marginBottom: 6,
    },
    caption: {
        color: '#333',
        fontSize: 12,
    },
    ladderWrapper: {
        position: 'relative',
    },
    ladderScrollContent: {
        paddingHorizontal: 0,
        paddingBottom: 4,
    },
    ladderContent: {
        // position: 'relative',
        // paddingHorizontal: scale(10),
    },
    progressLineContainer: {
        position: 'absolute',
        top: 40,
        height: scale(4),
        zIndex: 0,
    },
    progressLineBackground: {
        position: 'absolute',
        width: '100%',
        height: scale(4),
        backgroundColor: '#E0E0E0',
        borderRadius: 2,
    },
    progressLineFilled: {
        position: 'absolute',
        height: scale(4),
        borderRadius: 4,
    },
    milestonesRow: {
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
        zIndex: 1,
        paddingHorizontal: 0,
    },
    milestoneContainer: {
        alignItems: 'center',
        flex: 0,
        width: scale(30),
        height: scale(80),
        position: 'relative',
        paddingVertical: 8,
        justifyContent: 'center',
    },
    firstMilestone: {
        flex: 0,
        alignItems: 'flex-start',
        // minWidth: 40,
    },
    lastMilestone: {
        flex: 0,
        alignItems: 'flex-end',
        // minWidth: 40,
    },
    headerContainer: {
        alignItems: 'center',
        justifyContent: 'flex-end',
        height: scale(30),
        paddingBottom: scale(2),
    },
    footerContainer: {
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: scale(30),
        paddingTop: scale(2),
    },
    mgContainer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    mgText: {
        fontSize: 11,
        fontWeight: '700',
        color: '#333',
    },
    infoIcon: {
        width: scale(14),
        height: scale(14),
        borderRadius: 7,
        backgroundColor: '#666',
        justifyContent: 'center',
        alignItems: 'center',
    },
    rewardText: {
        fontSize: 9,
        fontWeight: '700',
        color: '#666',
    },
    rewardTextCompleted: {
    },
    iconContainer: {
        justifyContent: 'center',
        alignItems: 'center',
        height: scale(20),
    },
    circleIcon: {
        marginLeft: 2,
        marginTop: 4,
        width: scale(14),
        height: scale(14),
        borderRadius: scale(7),
    },
    tickIcon: {
        width: scale(22),
        height: scale(22),
        borderRadius: 11,
        justifyContent: 'center',
        alignItems: 'center',
    },
    lockIcon: {
        width: scale(22),
        height: scale(22),
        borderRadius: 11,
        backgroundColor: '#F5F5F5',
        borderWidth: 2,
        borderColor: '#E0E0E0',
        justifyContent: 'center',
        alignItems: 'center',
    },
    lineIcon: {
        width: scale(2),
        height: scale(18),
        backgroundColor: '#666',
    },
    footerText: {
        fontSize: 13,
        fontWeight: '700',
        color: '#000',
        textAlign: 'center',
    },
    footerProgress: {
        fontSize: 10,
        fontWeight: '700',
        color: '#000',
        textAlign: 'center',
    },
    progressLabelContainer: {
        position: 'absolute',
        top: 32,
        width: 36,
        paddingHorizontal: 8,
        paddingVertical: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.68)',
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#666',
        shadowOpacity: 0.05,
        shadowOffset: { width: 0, height: 1 },
        shadowRadius: 1,
        elevation: 1,
        zIndex: 5
    },
    connector: {
        flex: 0,
        width: scale(30),
        height: 4,
        backgroundColor: 'transparent',
    },
    tripCompleted: {
        fontSize: 12,
        color: '#000',
        fontWeight: '600',
    },
    tooltipWrapper: {
        position: 'relative',
    },
    tooltip: {
        position: 'absolute',
        bottom: 22,
        left: -30,
        backgroundColor: '#333',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 8,
        minWidth: 100,
        maxWidth: 150,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
        elevation: 5,
        zIndex: 1000,
    },
    tooltipText: {
        fontSize: 11,
        color: '#FFF',
        textAlign: 'center',
        lineHeight: 14,
    },
    singleMilestone: {
        width: '100%',
        alignItems: 'center',
    },
    tooltipArrow: {
        position: 'absolute',
        bottom: -6,
        left: '50%',
        marginLeft: -6,
        width: 0,
        height: 0,
        borderLeftWidth: 6,
        borderRightWidth: 6,
        borderTopWidth: 6,
        borderLeftColor: 'transparent',
        borderRightColor: 'transparent',
        borderTopColor: '#333',
    },
    bottomStrip: {
        borderBottomLeftRadius: 24,
        borderBottomRightRadius: 24,
        paddingVertical: 10,
        paddingHorizontal: 16,
    },
    bottomStripText: {
        color: '#FFF',
        fontSize: 14,
        lineHeight: 18,
        fontWeight: '600',
        textAlign: 'center',
    },
    captionRow: {
        flexDirection: 'row',
    },
    captionLabel: {
        fontSize: 12,
        color: '#000',
        fontWeight: '600',
    },
    captionValue: {
        fontSize: 10,
        color: '#000',
        fontWeight: '600',
    },
    scrollWrapper: {
        position: 'relative',
    },
    scrollHint: {
        backgroundColor: 'rgba(255,255,255,0.9)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    scrollHintText: {
        fontSize: 16,
        fontWeight: '700',
        color: '#666',
    },
});

export default OfferWidgetWidget;